import { useI18n } from "./useI18n";
import { Ref, ref } from "vue";
import { modalController, toastController } from "@ionic/vue";
import { useRouter } from "vue-router";
import { Execution, Indicator, Instance, Routine, TemplateStep } from "@/types";
import { useRoutinesStore } from "@/store/routines";
import { useInstancesStore } from "@/store/instances";
import { useExecutionsStore } from "@/store/executions";
import { useTemplatesStore } from "@/store/templates";
import { useCommentsStore } from "@/store/comments";
import { useIndicatorsStore } from "@/store/indicators";
import { useNoOffline } from "@/composables/useNoOffline";
import { useDatapointsStore } from "@/store/datapoints";

import {
  endOfDay,
  format,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  subBusinessDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { useFabriqStore } from "@/store/fabriq";
import RoutineInstanceSettingsModalVue from "@/components/modals/RoutineInstanceSettingsModal.vue";
import { formatDateForRoutine } from "@/utils/routines";
import { localLogger } from "@/utils/localLogger";

export const useStartInstance = () => {
  const { t } = useI18n();
  const router = useRouter();
  const loading = ref(false);
  const loadingText: Ref<string | null> = ref(null);
  const { noOffline } = useNoOffline();
  const commentsStore = useCommentsStore();
  const routinesStore = useRoutinesStore();
  const instancesStore = useInstancesStore();
  const executionsStore = useExecutionsStore();
  const templatesStore = useTemplatesStore();
  const indicatorsStore = useIndicatorsStore();
  const fabriqStore = useFabriqStore();
  const datapointsStore = useDatapointsStore();

  interface RoutineInstanceSettings {
    teamId: number;
    auditee?: number | null;
    properties?: Record<string, number[]>;
  }

  const getIndicatorIds = (steps: Array<TemplateStep>) => {
    const ids: number[] = [];
    if (!steps?.length) return [];
    steps.forEach((el: TemplateStep) => {
      const { config } = el;
      const { indicatorId } = config;
      if (!indicatorId) return;
      ids.push(indicatorId);
    });
    return ids;
  };

  const loadIndicators = async (steps: Array<TemplateStep>) => {
    const indicatorsId = getIndicatorIds(steps);
    const promises: Promise<Indicator>[] = indicatorsId.map((id: number) =>
      indicatorsStore.load(id)
    );
    loadingText.value = t("routines.loader.indicators");
    return Promise.all(promises);
  };

  const loadDatapoints = async (indicators: Indicator[]) => {
    const promises = indicators.map((i) => {
      let fromDate = format(subBusinessDays(new Date(), 4), "yyyy-MM-dd");
      switch (i.frequency) {
        case "monthly":
          fromDate = format(
            startOfMonth(subMonths(new Date(), 1)),
            "yyyy-MM-dd"
          );
          break;

        case "weekly":
          fromDate = format(
            startOfISOWeek(subWeeks(new Date(), 1)),
            "yyyy-MM-dd"
          );
          break;

        default:
          break;
      }

      return datapointsStore.all({
        id: i.id,
        from_date: fromDate,
      });
    });
    loadingText.value = t("routines.loader.datapoints");
    await Promise.all(promises);
  };

  const loadExecution = async (
    instance: Instance,
    steps: Array<TemplateStep>
  ) => {
    if (typeof instance.id === "number") {
      commentsStore.all({ instance: instance.id });
    }
    const id = instance.id || instance.uuid;
    const execution = executionsStore.collection.find(
      (t: Execution) => t.instance === id
    );
    if (!execution) {
      await localLogger.log(`Create execution for instance ${id}`);
      loadingText.value = t("routines.loader.steps");
      await executionsStore.add(
        {
          current_step_index: 1,
          instance: instance.id || instance.uuid,
          start_date: formatDateForRoutine(new Date()),
        },
        steps
      );
    }
    const indicators = await loadIndicators(steps);
    await loadDatapoints(indicators);
    await localLogger.log(`Execution created for instance ${id}`);
    loadingText.value = t("routines.loader.goto");
  };

  const getRoutine = async (
    id: number,
    options: { forceRefresh?: boolean } = { forceRefresh: true }
  ) => {
    await localLogger.log(`Load routine ${id}`);
    return routinesStore.load(id, { forceRefresh: options.forceRefresh });
  };

  const getTemplate = async (
    id: number,
    options: { forceRefresh?: boolean } = { forceRefresh: true }
  ) => {
    await localLogger.log(`Load template ${id}`);
    return templatesStore.load(id, { forceRefresh: options.forceRefresh });
  };

  const getTeamId = async (instance: Instance, routine: Routine) => {
    if (instance.scheduler?.team) return instance.scheduler?.team;
    const { teamId } = await getSettings(routine);
    return teamId;
  };

  const startScheduledInstance = async (instance: Instance) => {
    loading.value = true;
    await localLogger.log(
      `startScheduledInstance ID: ${instance.id} UUID: ${instance.uuid}`
    );
    loadingText.value = t("routines.loader.version");
    const routine: Routine = await getRoutine(instance.routine_id, {
      forceRefresh: !instance.scheduler?.has_started,
    });
    const template = await getTemplate(
      instance.template_id || routine.template_id,
      { forceRefresh: !instance.scheduler?.has_started }
    );
    const { steps } = template;
    loadingText.value = t("routines.loader.instance");
    if (typeof instance.id === "string") {
      // This is the specific case for scheduled instances
      // that are not yet created in the backend
      // The backend is replying with a "mocked" instance
      // that is not yet saved
      const teamId = await getTeamId(instance, routine);
      const today = new Date();
      const scheduleId = instance.schedule;
      const scheduler = routine.schedule.find(
        (rs) => rs.id === scheduleId
      )?.scheduler;
      const newInstance = await instancesStore.create({
        ...instance,
        team_id: teamId,
        template_id: instance.template_id || routine.template_id,
        scheduler: {
          ...scheduler,
          has_started: true,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezone_offset: (today.getTimezoneOffset() / 60) * -1,
      });
      await loadExecution(newInstance, steps);
      router.push(`/instances/${newInstance.uuid}`);
    } else if (instance.id) {
      await loadExecution(instance, steps);
      router.push(`/instances/${instance.uuid}`);
    } else {
      resetLoading();
      await localLogger.log(
        `startScheduledInstance ERROR ID: ${instance.id} UUID: ${instance.uuid}`
      );
      const toast = await toastController.create({
        message: t("common.unknownError"),
        duration: 2000,
      });
      toast.present();
      return;
    }
    await localLogger.log(
      `startScheduledInstance END ID: ${instance.id} UUID: ${instance.uuid}`
    );
    resetLoading();
  };

  const shouldOpenSettingsModal = (routine: Routine) => {
    if (routine.teams.length > 1) return true;
    return routine.config?.promptScopeSelectionModal;
  };

  const getSettings = (routine: Routine): Promise<RoutineInstanceSettings> => {
    return new Promise(async (resolve, reject) => {
      if (!routine.teams.length) reject("No team in routine");
      if (!shouldOpenSettingsModal(routine)) {
        return resolve({ teamId: routine.teams[0] });
      }
      const modal = await modalController.create({
        component: RoutineInstanceSettingsModalVue,
        canDismiss: true,
        mode: "ios",
        componentProps: {
          routine,
          onDone: (settings: RoutineInstanceSettings) => {
            resolve(settings);
            modal.dismiss();
          },
          onCancel: () => {
            reject();
            modal.dismiss();
          },
        },
      });
      modal.present();
    });
  };

  const startRoutine = async (routineInStore: Routine) => {
    if (!fabriqStore.online) return noOffline();
    await localLogger.log(
      `startRoutine ID: ${routineInStore.id} UUID: ${routineInStore.uuid}`
    );
    try {
      const routineId =
        typeof routineInStore.id === "string"
          ? parseInt(routineInStore.id)
          : routineInStore.id;
      if (!routineId) {
        const routineIdError = new Error("Routine id should be defined");
        fabriqStore.reportSentry(routineIdError, {
          routineId,
        });
        throw routineIdError;
      }
      const routine = await getRoutine(routineId, { forceRefresh: true });

      if (!routine) {
        const routineNotFoundError = new Error(
          `Routine with id ${routineId} not found`
        );
        fabriqStore.reportSentry(routineNotFoundError, {
          routineId,
        });
        throw routineNotFoundError;
      }
      const settings = await getSettings(routine);
      if (!settings) {
        const routineSettingsNotFoundError = new Error(
          `Routine settings not found for routine with id ${routineId}`
        );
        fabriqStore.reportSentry(routineSettingsNotFoundError, {
          routine,
        });
        throw routineSettingsNotFoundError;
      }
      loadingText.value = t("routines.loader.version");
      const template = await getTemplate(routine.template_id);
      if (!template) {
        const routineTemplateNotFoundError = new Error(
          `Routine template with id ${routine.template_id} not found`
        );
        fabriqStore.reportSentry(routineTemplateNotFoundError, {
          routine,
        });
        throw routineTemplateNotFoundError;
      }
      const { steps } = template;
      loadingText.value = t("routines.loader.instance");
      loading.value = true;
      const today = new Date();
      const addedInstance = await instancesStore.add({
        all_day: true,
        start_date: startOfDay(today),
        end_date: endOfDay(today),
        has_started: true,
        instructions: "",
        is_done: false,
        is_dst: true,
        users: [fabriqStore.user?.id],
        routine_id: routine.id,
        template_id: routine.template_id,
        team_id: settings.teamId
          ? JSON.parse(JSON.stringify(settings.teamId))
          : undefined,
        auditee: settings.auditee
          ? JSON.parse(JSON.stringify(settings.auditee))
          : undefined,
        properties: settings.properties
          ? JSON.parse(JSON.stringify(settings.properties))
          : undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezone_offset: (today.getTimezoneOffset() / 60) * -1,
      });
      await instancesStore.save();
      const instance = instancesStore.collection.find(
        (i: Instance) => i.uuid === addedInstance.uuid
      );
      loadingText.value = t("routines.loader.steps");
      await executionsStore.add({
        current_step_index: 1,
        instance: instance.id,
        start_date: formatDateForRoutine(today),
      });
      await executionsStore.save();

      const indicators = await loadIndicators(steps);
      if (!indicators) {
        const indicatorsNotFoundError = new Error(
          `Indicators not found for routine with id ${routineId}`
        );
        fabriqStore.reportSentry(indicatorsNotFoundError, {
          routine,
        });
        throw indicatorsNotFoundError;
      }
      await loadDatapoints(indicators);

      fabriqStore.setLoading(false);
      router.push(`/instances/${instance.uuid}`);
      await localLogger.log(
        `startRoutine END ID: ${routineInStore.id} UUID: ${routineInStore.uuid}`
      );
      resetLoading();
    } catch (e) {
      console.error(e);
      await localLogger.log(
        `startRoutine ERROR ID: ${routineInStore.id} UUID: ${
          routineInStore.uuid
        } ERROR: ${JSON.stringify(e)}`
      );
      const toast = await toastController.create({
        message: t("routines.startInstanceError"),
        duration: 2000,
        color: "danger",
      });
      loading.value = false;
      fabriqStore.setLoading(false);
      toast.present();
    }
  };

  let timer: NodeJS.Timeout;
  const resetLoading = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      loading.value = false;
      loadingText.value = "";
    }, 1000);
  };

  return {
    loading,
    loadingText,
    startScheduledInstance,
    startRoutine,
  };
};
